// src/features/game/components/GameTemplateCard.tsx

import React from 'react';

import {
  Edit2,
  Play,
  Trash2,
} from 'lucide-react';

import type {
  GameTemplate,
} from '../types/game.types';

import {
  formatCurrency,
  formatNumber,
} from '../../../shared/utils/formatUtils';

interface GameTemplateCardProps {
  template: GameTemplate;

  onCreate: () => void;

  onEdit: (
    gameId: number,
  ) => void;

  onDelete: (
    gameId: number,
    gameName: string,
  ) => void | Promise<void>;

  isCreating?: boolean;
  isDeleting?: boolean;
  disabled?: boolean;
}

const GameTemplateCard:
  React.FC<GameTemplateCardProps> = ({
    template,
    onCreate,
    onEdit,
    onDelete,
    isCreating = false,
    isDeleting = false,
    disabled = false,
  }) => {
  return (
    <>
      {/* Desktop */}
      <div
        className="
          hidden
          min-w-[900px]
          grid-cols-[minmax(240px,2fr)_130px_140px_120px_110px_100px_100px]
          items-center
          transition-colors
          hover:bg-gray-50
          md:grid
        "
      >
        {/* 게임명 */}
        <div className="px-5 py-5">
          <div className="font-semibold text-gray-900">
            {template.gameName}
          </div>
        </div>

        {/* 바이인 */}
        <div className="px-4 py-5 text-center text-sm font-medium text-gray-800">
          {formatCurrency(
            template.gamePrice,
          )}
        </div>

        {/* 시작 스택 */}
        <div className="px-4 py-5 text-center text-sm font-medium text-gray-800">
          {formatNumber(
            template.gameStack,
          )}
        </div>

        {/* 바이인 제한 */}
        <div className="px-4 py-5 text-center text-sm font-medium text-gray-800">
          {template.gameLimit}회
        </div>

        {/* 생성 */}
        <div className="px-3 py-5 text-center">
          <button
            type="button"
            onClick={
              onCreate
            }
            disabled={
              disabled
            }
            className="
              inline-flex
              min-w-[74px]
              items-center
              justify-center
              gap-1.5
              whitespace-nowrap
              rounded-md
              bg-emerald-500
              px-3 py-2
              text-sm
              font-medium
              text-white
              transition
              hover:bg-emerald-600
              disabled:cursor-not-allowed
              disabled:opacity-50
            "
          >
            <Play
              size={14}
            />

            {isCreating
              ? '생성 중'
              : '생성'}
          </button>
        </div>

        {/* 수정 */}
        <div className="px-3 py-5 text-center">
          <button
            type="button"
            onClick={() =>
              onEdit(
                template.gameId,
              )
            }
            disabled={
              disabled
            }
            className="
              inline-flex
              min-w-[70px]
              items-center
              justify-center
              gap-1.5
              whitespace-nowrap
              rounded-md
              bg-blue-500
              px-3 py-2
              text-sm
              font-medium
              text-white
              transition
              hover:bg-blue-600
              disabled:cursor-not-allowed
              disabled:opacity-50
            "
          >
            <Edit2
              size={14}
            />

            수정
          </button>
        </div>

        {/* 삭제 */}
        <div className="px-3 py-5 text-center">
          <button
            type="button"
            onClick={() =>
              void onDelete(
                template.gameId,
                template.gameName,
              )
            }
            disabled={
              disabled
            }
            className="
              inline-flex
              min-w-[70px]
              items-center
              justify-center
              gap-1.5
              whitespace-nowrap
              rounded-md
              bg-gray-500
              px-3 py-2
              text-sm
              font-medium
              text-white
              transition
              hover:bg-red-500
              disabled:cursor-not-allowed
              disabled:opacity-50
            "
          >
            <Trash2
              size={14}
            />

            {isDeleting
              ? '삭제 중'
              : '삭제'}
          </button>
        </div>
      </div>

      {/* Mobile */}
      <div className="p-4 md:hidden">
        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <h3 className="mb-4 font-semibold text-gray-900">
            {template.gameName}
          </h3>

          <div className="mb-4 grid grid-cols-3 gap-3 text-center">
            <div>
              <div className="text-xs text-gray-500">
                바이인
              </div>

              <div className="mt-1 text-sm font-medium text-gray-900">
                {formatCurrency(
                  template.gamePrice,
                )}
              </div>
            </div>

            <div>
              <div className="text-xs text-gray-500">
                시작 스택
              </div>

              <div className="mt-1 text-sm font-medium text-gray-900">
                {formatNumber(
                  template.gameStack,
                )}
              </div>
            </div>

            <div>
              <div className="text-xs text-gray-500">
                제한
              </div>

              <div className="mt-1 text-sm font-medium text-gray-900">
                {
                  template.gameLimit
                }
                회
              </div>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-2">
            {/* 생성 */}
            <button
              type="button"
              onClick={
                onCreate
              }
              disabled={
                disabled
              }
              className="
                flex
                items-center
                justify-center
                gap-1
                whitespace-nowrap
                rounded-lg
                bg-emerald-500
                px-3 py-2
                text-sm
                font-medium
                text-white
                transition
                hover:bg-emerald-600
                disabled:cursor-not-allowed
                disabled:opacity-50
              "
            >
              <Play
                size={14}
              />

              {isCreating
                ? '생성 중'
                : '생성'}
            </button>

            {/* 수정 */}
            <button
              type="button"
              onClick={() =>
                onEdit(
                  template.gameId,
                )
              }
              disabled={
                disabled
              }
              className="
                flex
                items-center
                justify-center
                gap-1
                whitespace-nowrap
                rounded-lg
                bg-blue-500
                px-3 py-2
                text-sm
                font-medium
                text-white
                transition
                hover:bg-blue-600
                disabled:cursor-not-allowed
                disabled:opacity-50
              "
            >
              <Edit2
                size={14}
              />

              수정
            </button>

            {/* 삭제 */}
            <button
              type="button"
              onClick={() =>
                void onDelete(
                  template.gameId,
                  template.gameName,
                )
              }
              disabled={
                disabled
              }
              className="
                flex
                items-center
                justify-center
                gap-1
                whitespace-nowrap
                rounded-lg
                bg-gray-500
                px-3 py-2
                text-sm
                font-medium
                text-white
                transition
                hover:bg-red-500
                disabled:cursor-not-allowed
                disabled:opacity-50
              "
            >
              <Trash2
                size={14}
              />

              {isDeleting
                ? '삭제 중'
                : '삭제'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default GameTemplateCard;